import { ActivityEntityName, TaskLinkType } from '@jms/model';
import type { Task, TaskLinkEntity } from '@jms/model';
import type { EntityRecordParent } from '@workbuddy/services/entities/entity.manifest.relations';

// Link types that name a record an entity manifest can own; a Stage is stored as WorkOrder.
const parentEntityByLinkType: Partial<Record<TaskLinkType, ActivityEntityName>> = {
  [TaskLinkType.Job]: ActivityEntityName.Job,
  [TaskLinkType.WorkOrder]: ActivityEntityName.WorkOrder,
  [TaskLinkType.Project]: ActivityEntityName.Project,
  [TaskLinkType.Work]: ActivityEntityName.Work,
  [TaskLinkType.Opportunity]: ActivityEntityName.Opportunity,
  [TaskLinkType.JobTemplate]: ActivityEntityName.JobTemplate,
  [TaskLinkType.Contact]: ActivityEntityName.Contact,
  [TaskLinkType.Employee]: ActivityEntityName.Employee,
  [TaskLinkType.Task]: ActivityEntityName.Task,
};

/**
 * A stored task's parents, nearest first: its direct `parent`, then the ancestors the server stamped
 * on `links`. Relationship-agnostic on purpose — a capability picks the first that declares one.
 */
export function resolveTaskRecordParent(task: Pick<Task, 'parent' | 'links'>): readonly EntityRecordParent[] {

  const seen = new Set<string>();
  return [task.parent, ...(task.links ?? [])].flatMap(link => {
    const parent = toRecordParent(link);
    if (!parent || seen.has(`${parent.entity}:${parent.id}`)) return [];
    seen.add(`${parent.entity}:${parent.id}`);
    return [parent];
  });
}

/** The stage a stored task sits on, its own parent or the stage the server stamped on `links`; mounts date its due day in that stage's site zone (WBS-12244). */
export function taskStageId(task: Pick<Task, 'parent' | 'links'>): string | undefined {

  return [task.parent, ...(task.links ?? [])].find(link => link?.type === TaskLinkType.WorkOrder)?._id;
}

function toRecordParent(link: TaskLinkEntity | null | undefined): EntityRecordParent | null {

  const entity = link?.type ? parentEntityByLinkType[link.type] : undefined;
  return entity && link?._id ? { entity, id: link._id } : null;
}
